import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { KestrelInspectionType, RolodexEntryType } from 'types'

import {
  buildThreeDModelRows,
  type ThreeDModelRow,
} from './buildThreeDModelRows'

const SITE_ENTRY_LIMIT = 1000
const LINK_ENTRY_LIMIT = 10000

export interface UseThreeDModelRowsResult {
  rows: ThreeDModelRow[]
  isLoading: boolean
  error: unknown
}

/** Every asset at the site with a Matterport walkthrough or a drone scan model. */
export function useThreeDModelRows(
  siteId: string | undefined
): UseThreeDModelRowsResult {
  const {
    units,
    matterportSpaces,
    kestrelModels,
    kestrelModelUnits,
    isLoading,
    error,
  } = useRolodexEntries(
    siteId
      ? [
          {
            type: RolodexEntryType.UNIT,
            tags: { site: siteId },
            limit: SITE_ENTRY_LIMIT,
          },
          {
            type: RolodexEntryType.MATTERPORT_SPACE,
            tags: { site: siteId },
            limit: SITE_ENTRY_LIMIT,
          },
          {
            type: RolodexEntryType.KESTREL_MODEL,
            tags: { site: siteId },
            filters: [
              {
                key: 'inspection_type',
                op: '=',
                value: KestrelInspectionType.SITEVIEW,
              },
            ],
            limit: SITE_ENTRY_LIMIT,
          },
          {
            type: RolodexEntryType.KESTREL_MODEL_UNIT,
            tags: { site: siteId },
            limit: LINK_ENTRY_LIMIT,
          },
        ]
      : undefined,
    { allowCache: true }
  )

  const rows = useMemo(
    () =>
      buildThreeDModelRows({
        units,
        matterportSpaces,
        siteViewModels: kestrelModels,
        kestrelModelUnits,
      }),
    [units, matterportSpaces, kestrelModels, kestrelModelUnits]
  )

  return { rows, isLoading, error }
}
